import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  BankOutlined,
  CalendarOutlined,
  ContainerOutlined,
  DeleteOutlined,
  DownloadOutlined,
  LinkOutlined,
  NumberOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../services/axiosInstance";
import { DELETE_RAKE, GET_RAKE } from "../../services/apiEndPoints";
import { downloadRakeReport } from "../../services/rakeReport";
import { IRakeDetail, IRakeMaster } from "../types/rake.types";

const { Title, Text } = Typography;

const RakeDetailsComponent = () => {
  const { rakeMasterId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rakeMaster, setRakeMaster] = useState<IRakeMaster | null>(null);
  const [rakeDetails, setRakeDetails] = useState<IRakeDetail[]>([]);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchRake = useCallback(async () => {
    if (!rakeMasterId) return;
    try {
      setLoading(true);
      const response = await axios.get(GET_RAKE(rakeMasterId));
      const data = response?.data?.data ?? response?.data;
      setRakeMaster(data?.rakeMaster ?? null);
      setRakeDetails(
        Array.isArray(data?.rakeDetails) ? data.rakeDetails : []
      );
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        message.error("Rake master not found");
      } else {
        message.error("Failed to fetch rake");
      }
    } finally {
      setLoading(false);
    }
  }, [rakeMasterId]);

  useEffect(() => {
    fetchRake();
  }, [fetchRake]);

  const handleDownloadReport = useCallback(async () => {
    if (!rakeMaster) return;
    setReportLoading(true);
    try {
      const date = rakeMaster.date
        ? moment(rakeMaster.date).format("YYYY-MM-DD")
        : undefined;
      await downloadRakeReport(date);
      message.success("Rake report downloaded");
    } catch (error: any) {
      message.error(error?.message || "Failed to download rake report");
    } finally {
      setReportLoading(false);
    }
  }, [rakeMaster]);

  const handleDelete = useCallback(() => {
    if (!rakeMasterId) return;
    Modal.confirm({
      title: "Delete Rake",
      content:
        "Delete this rake? This removes the master and all its wagon details.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await axios.delete(DELETE_RAKE(rakeMasterId));
          message.success("Rake deleted successfully");
          navigate("/rakes");
        } catch {
          message.error("Failed to delete rake");
        }
      },
    });
  }, [rakeMasterId, navigate]);

  const totals = useMemo(() => {
    return rakeDetails.reduce(
      (acc, r) => {
        acc.actualBags += Number(r.actualBags || 0);
        acc.rrBags += Number(r.rrBags || 0);
        acc.chargeWeight += Number(r.chargeWeight || 0);
        acc.actualWeight += Number(r.actualWeight || 0);
        return acc;
      },
      { actualBags: 0, rrBags: 0, chargeWeight: 0, actualWeight: 0 }
    );
  }, [rakeDetails]);

  const shortageWeight = totals.chargeWeight - totals.actualWeight;

  const columns = useMemo(
    () => [
      {
        title: "S.No",
        dataIndex: "sno",
        key: "sno",
        width: 70,
        sorter: (a: IRakeDetail, b: IRakeDetail) => a.sno - b.sno,
      },
      {
        title: "Wagon No",
        dataIndex: "wagonNo",
        key: "wagonNo",
        render: (wagonNo: string) =>
          wagonNo ? (
            <Text strong style={{ fontSize: 13 }}>
              {wagonNo}
            </Text>
          ) : (
            <Text type="secondary">--</Text>
          ),
      },
      {
        title: "Type",
        dataIndex: "typeOfWagon",
        key: "typeOfWagon",
        render: (type: string) =>
          type ? <Tag color="geekblue">{type}</Tag> : <Text type="secondary">--</Text>,
      },
      {
        title: "Variety",
        dataIndex: "variety",
        key: "variety",
        render: (variety: string) =>
          variety ? <Tag color="purple">{variety}</Tag> : <Text type="secondary">--</Text>,
      },
      {
        title: "Actual Bags",
        dataIndex: "actualBags",
        key: "actualBags",
        align: "right" as const,
        render: (v: number) => v?.toLocaleString() ?? 0,
      },
      {
        title: "RR Bags",
        dataIndex: "rrBags",
        key: "rrBags",
        align: "right" as const,
        render: (v: number) => v?.toLocaleString() ?? 0,
      },
      {
        title: "Charge Weight (kg)",
        dataIndex: "chargeWeight",
        key: "chargeWeight",
        align: "right" as const,
        render: (v: number) => `${(v ?? 0).toLocaleString()} kg`,
      },
      {
        title: "Actual Weight (kg)",
        dataIndex: "actualWeight",
        key: "actualWeight",
        align: "right" as const,
        render: (v: number) => `${(v ?? 0).toLocaleString()} kg`,
      },
      {
        title: "Shortage (kg)",
        key: "shortage",
        align: "right" as const,
        render: (_: any, r: IRakeDetail) => {
          const diff = Number(r.chargeWeight || 0) - Number(r.actualWeight || 0);
          if (!diff) return <Text type="secondary">0 kg</Text>;
          return (
            <Text style={{ color: diff > 0 ? "#dc2626" : "#10b981" }}>
              {diff.toLocaleString()} kg
            </Text>
          );
        },
      },
      {
        title: "Remarks",
        dataIndex: "remarks",
        key: "remarks",
        render: (remarks: string) =>
          remarks ? (
            <Text style={{ fontSize: 13 }}>{remarks}</Text>
          ) : (
            <Text type="secondary">--</Text>
          ),
      },
    ],
    []
  );

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading rake...</Text>
        </div>
      </div>
    );
  }

  if (!rakeMaster) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/rakes")}
          style={{ marginBottom: 16 }}
        >
          Back to Rakes
        </Button>
        <Card>
          <div style={{ textAlign: "center", padding: 40 }}>
            <ContainerOutlined
              style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }}
            />
            <div style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>
              Rake not found
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const summaryCards = [
    {
      label: "Wagons",
      value: rakeDetails.length,
      color: "#1677ff",
      bg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
      border: "#93c5fd",
    },
    {
      label: "Actual Bags",
      value: totals.actualBags.toLocaleString(),
      color: "#8b5cf6",
      bg: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
      border: "#c4b5fd",
    },
    {
      label: "RR Bags",
      value: totals.rrBags.toLocaleString(),
      color: "#10b981",
      bg: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
      border: "#6ee7b7",
    },
    {
      label: "Actual Weight (kg)",
      value: `${totals.actualWeight.toLocaleString()} kg`,
      color: "#f59e0b",
      bg: "linear-gradient(135deg, #fffbeb, #fef3c7)",
      border: "#fcd34d",
    },
    {
      label: "Charge Weight (kg)",
      value: `${totals.chargeWeight.toLocaleString()} kg`,
      color: "#0ea5e9",
      bg: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
      border: "#7dd3fc",
    },
    {
      label: "Shortage (kg)",
      value: `${shortageWeight.toLocaleString()} kg`,
      color: shortageWeight > 0 ? "#dc2626" : "#10b981",
      bg:
        shortageWeight > 0
          ? "linear-gradient(135deg, #fef2f2, #fee2e2)"
          : "linear-gradient(135deg, #f0fdf4, #dcfce7)",
      border: shortageWeight > 0 ? "#fca5a5" : "#6ee7b7",
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Space align="center" size={8}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/rakes")}
            >
              Back
            </Button>
            <div>
              <Title level={4} className="page-title" style={{ margin: 0 }}>
                <ContainerOutlined style={{ marginRight: 8, color: "#1677ff" }} />
                Rake {rakeMaster.rrNumber || rakeMaster.rakeMasterId}
              </Title>
              <Text className="page-subtitle">
                Master record and wagon-level details
              </Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={reportLoading}
              onClick={handleDownloadReport}
            >
              Download Report
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchRake}>
              Refresh
            </Button>
            {rakeMaster.s3Url && (
              <a
                href={rakeMaster.s3Url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button icon={<LinkOutlined />}>View Source</Button>
              </a>
            )}
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              Delete
            </Button>
          </Space>
        </Col>
      </Row>

      <Card
        style={{ marginBottom: 20, borderRadius: 12 }}
        styles={{ body: { padding: 20 } }}
      >
        <Descriptions
          column={{ xs: 1, sm: 2, md: 3 }}
          size="small"
          labelStyle={{ color: "#64748b", fontSize: 12 }}
          contentStyle={{ fontSize: 13 }}
        >
          <Descriptions.Item
            label={
              <>
                <NumberOutlined /> RR Number
              </>
            }
          >
            {rakeMaster.rrNumber || "--"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <NumberOutlined /> FNR Number
              </>
            }
          >
            {rakeMaster.fnrNumber || "--"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <BankOutlined /> Company
              </>
            }
          >
            {rakeMaster.company ? (
              <Tag color="purple">{rakeMaster.company}</Tag>
            ) : (
              "--"
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined /> Rake Date
              </>
            }
          >
            {rakeMaster.date
              ? moment(rakeMaster.date).format("MMM D, YYYY")
              : "--"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined /> Uploaded
              </>
            }
          >
            {moment(rakeMaster.createdAt).format("MMM D, YYYY")} (
            {moment(rakeMaster.createdAt).fromNow()})
          </Descriptions.Item>
          <Descriptions.Item label="Rake ID">
            <Text code style={{ fontSize: 12 }}>
              {rakeMaster.rakeMasterId}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {summaryCards.map((card) => (
          <Col key={card.label} xs={12} sm={8} md={4}>
            <Card
              className="stat-card"
              style={{ background: card.bg, border: `1px solid ${card.border}` }}
              styles={{ body: { padding: "14px 16px" } }}
            >
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: card.color,
                  lineHeight: 1.1,
                }}
              >
                {card.value}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#64748b",
                  fontWeight: 500,
                  marginTop: 4,
                }}
              >
                {card.label}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Table<IRakeDetail>
        rowKey={(r) => `${r.rakeMasterId}-${r.sno}`}
        size="middle"
        columns={columns as any}
        dataSource={rakeDetails}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100"],
          showTotal: (total, range) => (
            <Text style={{ color: "#64748b", fontSize: 13 }}>
              Showing <Text strong>{range[0]}-{range[1]}</Text> of{" "}
              <Text strong>{total}</Text> wagon{total !== 1 ? "s" : ""}
            </Text>
          ),
        }}
        scroll={{ x: "max-content" }}
        locale={{
          emptyText: (
            <div style={{ padding: "40px 0" }}>
              <ContainerOutlined
                style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }}
              />
              <div style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>
                No wagon details
              </div>
            </div>
          ),
        }}
      />
    </div>
  );
};

export default RakeDetailsComponent;
